import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, AutoComplete, Button, Form, Input, Modal, Progress, Space, Tag, Typography } from "antd";
import { ArrowLeftOutlined, CheckOutlined, OrderedListOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { itemWeight, nextLocationCode, normaliseLocationCode, parseRackLayout, rackLetters, validateLocationCode, withWeightLabel } from "functions/pickRoute";
import { filterVakFillOptions, vakFillOptions, vakFillProgress } from "functions/vakFill";
import { SHOP_COLORS } from "../product-data/constants";

const { Text } = Typography;

/** Rows on screen at once; typing narrows it down further. */
const MAX_ROWS = 50;

const ShopTags = ({ shops }) =>
  shops.map((shop) => (
    <Tag key={shop} color={SHOP_COLORS[shop] || "default"} style={{ marginInlineEnd: 4 }}>
      {shop}
    </Tag>
  ));

/**
 * "Vakken vullen": walk the vakken one by one - 1A, 1B ... in the order of the rack layout - and
 * say which article lies in each, with the keyboard only: a few letters, the arrows, Enter, and
 * the next vak is up. The article and its linked counterpart of the other shop move in together,
 * and a vak that does not exist yet is made (functions/vakFill). Enter without a search moves on
 * and leaves the vak as it is.
 */
const FillVakkenModal = ({ open, entries, catalogue, links, linksLoaded, zones, onFill, onSortRoute, onClose, describeRefusal }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("setup");
  const [startCode, setStartCode] = useState("1A");
  const [layoutText, setLayoutText] = useState("8");
  const [zone, setZone] = useState("");
  const [code, setCode] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastStep, setLastStep] = useState(null);
  const [counts, setCounts] = useState({ filled: 0, skipped: 0 });
  const searchRef = useRef(null);
  const rowRefs = useRef([]);

  useEffect(() => {
    if (!open) return;
    setPhase("setup");
    setHistory([]);
    setError(null);
    setLastStep(null);
    setCounts({ filled: 0, skipped: 0 });
  }, [open]);

  const layout = useMemo(() => parseRackLayout(layoutText), [layoutText]);
  const lettersPerRack = useMemo(() => (layout.ok ? rackLetters(startCode, layout.layout) : 8), [layout, startCode]);
  const options = useMemo(() => (open ? vakFillOptions(entries, catalogue, links) : []), [open, entries, catalogue, links]);
  const matches = useMemo(() => filterVakFillOptions(options, search), [options, search]);
  const shown = matches.slice(0, MAX_ROWS);
  const progress = vakFillProgress(options);
  const entry = useMemo(
    () => (code ? (entries || []).find((candidate) => normaliseLocationCode(candidate.location) === code) || null : null),
    [entries, code],
  );
  const upNext = code ? nextLocationCode(code, { lettersPerRack }) : null;
  const position = /^(\d+)([A-Z])$/.exec(code || "");
  const startValid = validateLocationCode(normaliseLocationCode(startCode)) && layout.ok;

  useEffect(() => setSelected(0), [search, code]);
  useEffect(() => {
    if (phase === "walk" && !busy && searchRef.current) searchRef.current.focus();
  }, [phase, code, busy]);
  useEffect(() => {
    const row = rowRefs.current[selected];
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const begin = () => {
    if (!startValid) return;
    setCode(normaliseLocationCode(startCode));
    setHistory([]);
    setSearch("");
    setPhase("walk");
  };

  const advance = () => {
    setHistory((previous) => [...previous, code]);
    setSearch("");
    if (upNext) setCode(upNext);
    else setPhase("done");
  };

  const write = async (items, label) => {
    setBusy(true);
    setError(null);
    try {
      const result = await onFill({ location: code, zone }, items);
      if (!result || result.ok === false) {
        setError(describeRefusal(result));
        return;
      }
      // A code that did not exist yet is made, also without an article: an empty basket is a vak
      setLastStep({ code, label, created: !!result.create, displaced: result.displaced ? result.displaced.label : null });
      setCounts((previous) => (items.length > 0 ? { ...previous, filled: previous.filled + 1 } : { ...previous, skipped: previous.skipped + 1 }));
      advance();
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };

  const choose = (option) => write(option.items, option.label);
  const moveOn = () => write([], null);

  const back = () => {
    if (history.length === 0) return;
    setCode(history[history.length - 1]);
    setHistory(history.slice(0, -1));
    setSearch("");
    setError(null);
  };

  const onKeyDown = (event) => {
    if (busy) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelected((index) => Math.min(index + 1, shown.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelected((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!search.trim()) moveOn();
      else if (shown[selected]) choose(shown[selected]);
    }
  };

  const placement = (option) => {
    if (!option.placedAt) return <Text type="secondary">{t("pick_route.fill_not_on_route")}</Text>;
    if (!option.placedAt.location) return <Text type="secondary">{t("pick_route.fill_on_route")}</Text>;
    return <Text type="warning">{t("pick_route.fill_lies_in", { location: option.placedAt.location })}</Text>;
  };

  const footers = {
    setup: [
      <Button key="cancel" onClick={onClose}>
        {t("cancel")}
      </Button>,
      <Button key="begin" type="primary" disabled={!startValid} onClick={begin}>
        {t("pick_route.fill_begin")}
      </Button>,
    ],
    walk: [
      <Button key="back" icon={<ArrowLeftOutlined />} disabled={busy || history.length === 0} onClick={back}>
        {t("pick_route.fill_previous")}
      </Button>,
      <Button key="stop" disabled={busy} onClick={() => setPhase("done")}>
        {t("pick_route.fill_stop")}
      </Button>,
      <Button key="next" type="primary" icon={<RightOutlined />} loading={busy} onClick={moveOn}>
        {t("pick_route.fill_next")}
      </Button>,
    ],
    done: [
      <Button key="close" onClick={onClose}>
        {t("pick_route.fill_close")}
      </Button>,
      <Button
        key="sort"
        type="primary"
        icon={<OrderedListOutlined />}
        onClick={() => {
          onSortRoute();
          onClose();
        }}
      >
        {t("pick_route.sort_by_location")}
      </Button>,
    ],
  };

  return (
    <Modal open={open} title={t("pick_route.fill_title")} width={680} onCancel={busy ? undefined : onClose} maskClosable={false} footer={footers[phase]} destroyOnClose>
      {phase !== "setup" && (
        <Progress
          percent={progress.total > 0 ? Math.round((progress.placed / progress.total) * 100) : 0}
          format={() => t("pick_route.fill_progress", progress)}
          size="small"
          className="mb-3"
        />
      )}

      {phase === "setup" && (
        <>
          <p style={{ color: "#6b6b6b" }}>{t("pick_route.fill_help")}</p>
          {!linksLoaded && <Alert type="warning" showIcon className="mb-3" message={t("pick_route.fill_links_missing")} />}
          <Form layout="vertical">
            <Space wrap align="start">
              <Form.Item label={t("pick_route.fill_start")}>
                <Input value={startCode} onChange={(event) => setStartCode(event.target.value)} onPressEnter={begin} style={{ width: 100, fontFamily: "monospace" }} />
              </Form.Item>
              <Form.Item
                label={t("pick_route.numbering_letters")}
                validateStatus={layout.ok ? "" : "error"}
                help={layout.ok ? null : t("pick_route.numbering_letters_invalid")}
              >
                <Input value={layoutText} onChange={(event) => setLayoutText(event.target.value)} onPressEnter={begin} style={{ width: 180 }} />
              </Form.Item>
              <Form.Item label={t("pick_route.fill_zone")}>
                <AutoComplete
                  value={zone}
                  onChange={setZone}
                  options={(zones || []).map((name) => ({ value: name }))}
                  placeholder={t("pick_route.fill_zone_placeholder")}
                  style={{ width: 180 }}
                />
              </Form.Item>
            </Space>
            <p style={{ color: "#6b6b6b", marginTop: -8 }}>{t("pick_route.numbering_letters_help")}</p>
          </Form>
        </>
      )}

      {phase === "walk" && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 40, lineHeight: 1 }} data-testid="fill-code">
              {code}
            </span>
            {!entry && <Tag color="blue">{t("pick_route.fill_new_vak")}</Tag>}
            {!!(entry ? entry.zone : zone) && <Tag>{entry ? entry.zone : zone}</Tag>}
            {position && (
              <Text type="secondary">
                {t("pick_route.fill_position", {
                  rack: position[1],
                  letter: position[2],
                  letters: typeof lettersPerRack === "function" ? lettersPerRack(Number(position[1])) : lettersPerRack,
                })}
              </Text>
            )}
            {!!upNext && <Text type="secondary">{t("pick_route.fill_up_next", { code: upNext })}</Text>}
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text strong>{t("pick_route.fill_contains")} </Text>
            {entry && (entry.products || []).length > 0 ? (
              (entry.products || []).map((product) => (
                <Tag key={`${product.shopSource}|${product.productId}|${itemWeight(product)}`} color={SHOP_COLORS[product.shopSource] || "default"}>
                  {withWeightLabel(product.name, itemWeight(product))}
                </Tag>
              ))
            ) : (
              <Text type="secondary">{t("pick_route.fill_empty")}</Text>
            )}
          </div>

          {!!lastStep && (
            <Alert
              type="success"
              showIcon
              icon={<CheckOutlined />}
              className="mb-2"
              message={
                lastStep.label
                  ? t("pick_route.fill_last", { location: lastStep.code, label: lastStep.label })
                  : lastStep.created
                    ? t("pick_route.fill_last_created", { location: lastStep.code })
                    : t("pick_route.fill_last_unchanged", { location: lastStep.code })
              }
              description={lastStep.displaced ? t("pick_route.fill_last_displaced", { label: lastStep.displaced }) : null}
            />
          )}
          {!!error && <Alert type="error" showIcon className="mb-2" message={error} />}

          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("pick_route.fill_search_placeholder")}
            disabled={busy}
            size="large"
            allowClear
          />
          <div role="listbox" style={{ maxHeight: 320, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0", borderRadius: 4 }}>
            {shown.length === 0 && (
              <div style={{ padding: 12 }}>
                <Text type="secondary">{t("pick_route.fill_nothing_found")}</Text>
              </div>
            )}
            {shown.map((option, index) => (
              <div
                key={option.key}
                ref={(node) => {
                  rowRefs.current[index] = node;
                }}
                role="option"
                aria-selected={index === selected}
                tabIndex={-1}
                onClick={() => !busy && choose(option)}
                onMouseEnter={() => setSelected(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  cursor: busy ? "default" : "pointer",
                  background: index === selected ? "#e6f4ff" : undefined,
                }}
              >
                <span style={{ flex: 1 }}>
                  <strong>{option.label}</strong>
                </span>
                <ShopTags shops={option.shops} />
                {placement(option)}
              </div>
            ))}
            {matches.length > shown.length && (
              <div style={{ padding: "6px 10px" }}>
                <Text type="secondary">{t("pick_route.fill_more", { count: matches.length - shown.length })}</Text>
              </div>
            )}
          </div>
          <p style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8, marginBottom: 0 }}>{t("pick_route.fill_keys")}</p>
        </>
      )}

      {phase === "done" && (
        <>
          <p>{t("pick_route.fill_done", counts)}</p>
          <p style={{ color: "#6b6b6b" }}>{t("pick_route.fill_done_sort")}</p>
        </>
      )}
    </Modal>
  );
};

export default FillVakkenModal;
